import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Anchor, Badge, Box, Text } from "@mantine/core";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { GiftCardListItem } from "../../types/giftCards";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { formatDateTime, DEFAULT_TIMEZONE } from "../../utils/timeUtils";
import { giftCardStatusColor } from "../../utils/giftCardStatus";
import SummaryStrip from "./shared/SummaryStrip";
import { SalesTable, SalesTableRow } from "./shared/SalesTable";
import SalesPagination from "./shared/SalesPagination";
import { useSalesDrawerSlot } from "./shared/salesDrawerSlot";
import GiftCardDetailDrawer from "./shared/GiftCardDetailDrawer";
import GiftCardsToolbar, {
  GiftCardStatusFilter,
  GiftCardClaimedFilter,
} from "./shared/GiftCardsToolbar";
import styles from "./shared/sales.module.css";

// Transaction Id (150), Date (150), Purchase Method (130), Code (130), Status
// (110), Buyer (flex), Attached to (flex), Amount (110px right), chevron (34).
// Scrolls horizontally below the floor.
const COLUMN_TEMPLATE = "150px 150px 130px 130px 110px 1fr 1fr 110px 34px";
const MIN_WIDTH = 1120;

/** How a card came to exist: an actual sale vs. a staff manual grant. */
const sourceLabel = (source: string): string =>
  source === "manual_grant" ? "Manual grant" : "Sale";

/**
 * A card is "claimed" once it has a holder (`client_uuid`) — NOT when it has a
 * `claimed_dt`. Manual grants are attached to their client at creation but never
 * get a `claimed_dt`, so the holder is the source of truth for claim state (the
 * `claimed_dt`-based check counted every manual grant as unclaimed).
 */
const isClaimed = (card: GiftCardListItem): boolean =>
  Boolean(card.client_uuid);

interface GiftCardsSubTabProps {
  refreshNonce?: number;
}

/**
 * Sales → Gift Cards. A salon-wide list of every gift card sold or granted at the
 * location: the originating sale reference + date, the card code, who bought it,
 * who it's attached to, its value, and its status. Fetches all cards once and
 * filters / paginates / summarizes client-side (mirroring the Purchases tab); the
 * tiles reflect the currently-filtered rows. A row opens a detail drawer.
 */
function GiftCardsSubTab({ refreshNonce }: GiftCardsSubTabProps) {
  const { businessInfo } = useSalon() as {
    businessInfo?: { id: string; salon_name?: string; timezone?: string };
  };
  // Render every datetime in the business location's timezone (not the
  // viewer's browser), falling back to the app default when the salon record
  // hasn't loaded a timezone yet.
  const timezone = businessInfo?.timezone || DEFAULT_TIMEZONE;
  const { listGiftCards } = useApp();
  const drawerSlot = useSalesDrawerSlot();

  const [cards, setCards] = useState<GiftCardListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<GiftCardStatusFilter>("all");
  const [claimedFilter, setClaimedFilter] =
    useState<GiftCardClaimedFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [openId, setOpenId] = useState<string | null>(null);

  // Monotonic request id so a slow response can't overwrite a newer fetch.
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!businessInfo?.id) return;
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const result = await listGiftCards(businessInfo.id);
      if (myReqId !== reqIdRef.current) return; // superseded
      setCards(result ?? []);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      console.error("Failed to load gift cards:", err);
      setError(true);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [businessInfo?.id, listGiftCards]);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, claimedFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (claimedFilter === "claimed" && !isClaimed(c)) return false;
      if (claimedFilter === "unclaimed" && isClaimed(c)) return false;
      if (q.length === 0) return true;
      const haystack = [
        c.code,
        c.purchaser_name,
        c.client_name,
        c.transaction_reference_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [cards, query, statusFilter, claimedFilter]);

  const summary = useMemo(() => {
    // "Value sold" is money that actually came in — real sales only, not the
    // face value of staff manual grants.
    const valueSold = filtered
      .filter((c) => c.source === "sale")
      .reduce((sum, c) => sum + (c.initial_amount || 0), 0);
    const outstanding = filtered
      .filter((c) => c.status === "active")
      .reduce((sum, c) => sum + (c.balance || 0), 0);
    const unclaimed = filtered.filter((c) => !isClaimed(c)).length;
    return [
      { label: "Gift cards", value: filtered.length.toLocaleString() },
      { label: "Value sold", value: formatCurrency(valueSold) },
      { label: "Outstanding", value: formatCurrency(outstanding) },
      { label: "Unclaimed", value: unclaimed.toLocaleString() },
    ];
  }, [filtered]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  const openCard = useMemo(
    () => (openId ? (cards.find((c) => c.id === openId) ?? null) : null),
    [openId, cards],
  );

  return (
    <Box>
      {error && (
        <Text size="xs" c="red" mb="sm">
          Couldn&apos;t load gift cards.{" "}
          <Anchor onClick={load} component="button" type="button" size="xs">
            Retry
          </Anchor>
        </Text>
      )}

      <SummaryStrip stats={summary} />

      <GiftCardsToolbar
        query={query}
        onQueryChange={setQuery}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        claimed={claimedFilter}
        onClaimedChange={setClaimedFilter}
        count={paged.length}
        total={filtered.length}
        loading={loading}
      />

      <SalesTable
        columnTemplate={COLUMN_TEMPLATE}
        minWidth={MIN_WIDTH}
        headers={[
          { label: "Transaction Id" },
          { label: "Date" },
          { label: "Purchase Method" },
          { label: "Code" },
          { label: "Status" },
          { label: "Buyer" },
          { label: "Attached to" },
          { label: "Amount", align: "right" },
          { label: "" },
        ]}
        loading={loading}
        isEmpty={filtered.length === 0}
        emptyText="No gift cards match these filters."
      >
        {paged.map((c) => (
          <SalesTableRow
            key={c.id}
            active={openId === c.id}
            onClick={() => setOpenId((cur) => (cur === c.id ? null : c.id))}
          >
            <Box className={styles.cellNum}>
              {c.transaction_reference_id || "—"}
            </Box>
            <Box className={styles.cellEllipsis}>
              {c.purchased_dt
                ? formatDateTime(c.purchased_dt, timezone, "MMM D · h:mm A")
                : "—"}
            </Box>
            <Box className={styles.cellEllipsis}>{sourceLabel(c.source)}</Box>
            <Box className={styles.cellNum}>{c.code}</Box>
            <Box>
              <Badge
                variant="light"
                color={giftCardStatusColor(c.status)}
                radius="sm"
                tt="capitalize"
              >
                {c.status}
              </Badge>
            </Box>
            <Box className={styles.cellEllipsis}>{c.purchaser_name || "—"}</Box>
            <Box className={styles.cellEllipsis}>
              {isClaimed(c) ? (
                c.client_name || "—"
              ) : (
                <Text component="span" c="dimmed" inherit>
                  Unclaimed
                </Text>
              )}
            </Box>
            <Box className={`${styles.cellRight} ${styles.cellNum}`}>
              {formatCurrency(c.initial_amount)}
            </Box>
          </SalesTableRow>
        ))}
      </SalesTable>

      <SalesPagination
        page={page}
        pageSize={pageSize}
        total={filtered.length}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        itemLabel="gift cards"
      />

      {openCard &&
        drawerSlot &&
        createPortal(
          <GiftCardDetailDrawer
            key={openCard.id}
            card={openCard}
            salonId={businessInfo?.id ?? ""}
            timezone={timezone}
            onClose={() => setOpenId(null)}
            onChanged={load}
          />,
          drawerSlot,
        )}
    </Box>
  );
}

export default GiftCardsSubTab;
